/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { ForwardedRef } from 'preact/compat';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  CurrentKeyDetail,
  Item,
  ItemActionDetail,
  ReorderDetail,
  SelectionDetail
} from '#UNSAFE_Collection';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type {
  ListContextMenuConfig,
  ListHandle,
  ListSelectionMode,
  ListScrollPosition
} from '../List.types';
import type { ScrollDetail, ViewportInfo } from '../List';

import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useContextMenu } from './useContextMenu';
import { useFocusHandling } from './useFocusHandling';
import { useItemReorder } from './useItemReorder';
import { useKeyboardHandling } from './useKeyboardHandling';
import { usePointerHandling } from './usePointerHandling';
import { useScrollHandling } from './useScrollHandling';
import { getElementFromCell, LIST_ROW_SELECTOR } from '../utils/ListDomUtils';
import { getScrollPosition } from '../utils/ListScrollUtils';

type UseInteractionManagerOptions<K, D> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  ref?: ForwardedRef<ListHandle<K>>;
  listId: string;
  listData: CollectionData<K, D>[];
  hasMore: boolean;
  isEmbedded: boolean;
  isLayoutRequired: boolean;
  scrollPositionOverride?: ListScrollPosition<K>;
  currentItemOverride?: Item<K>;
  onPersistCurrentItem?: (detail: CurrentKeyDetail<K>) => void;
  selectionMode?: ListSelectionMode;
  selectedKeys?: Keys<K>;
  onSelectionChange?: (detail: SelectionDetail<K>) => void;
  onItemAction?: (detail: ItemActionDetail<K, D>) => void;
  itemEnterKeyFocusBehavior: 'none' | 'focusWithin';
  contextMenuConfig?: ListContextMenuConfig<K, D>;
  onReorder?: (detail: ReorderDetail<K>) => void;
};

const _defaultDisabledRowInteractionKeys: ('enter' | 'space')[] = ['enter'];

/**
 * Hook that manages interactions and events on the Preact TableList
 */
export function useInteractionManager<K extends string | number, D>({
  rootRef,
  viewportInfo,
  ref,
  listId,
  listData,
  hasMore,
  isEmbedded,
  isLayoutRequired,
  scrollPositionOverride,
  currentItemOverride,
  onPersistCurrentItem,
  selectionMode,
  selectedKeys,
  onSelectionChange,
  onItemAction,
  itemEnterKeyFocusBehavior,
  contextMenuConfig,
  onReorder
}: UseInteractionManagerOptions<K, D>) {
  // whether row selection is triggered on 'enter' keypresses in addition to 'spacebar'
  const disabledRowInteractionKeys = isEmbedded ? undefined : _defaultDisabledRowInteractionKeys;

  // tracks the current selection extension element (if any)
  const selectionExtensionElementRef = useRef<HTMLElement>();

  // intercept onSelectionChange calls to ensure local selectionExtensionElementRef is cleared
  const onListSelectionChange = useMemo(() => {
    return onSelectionChange != null
      ? (detail: SelectionDetail<K>, isExtendableSelection: boolean) => {
          if (!isExtendableSelection) {
            selectionExtensionElementRef.current = undefined;
          }
          onSelectionChange(detail);
        }
      : undefined;
  }, [onSelectionChange]);

  // track pending scrolls to minimize 'flash' of focus rings when scrolling
  const [deferredScrollDetail, setDeferredScrollDetail] = useState<ScrollDetail<K> | undefined>(
    undefined
  );
  const deferredScrollDetailRef = useRef<ScrollDetail<K>>();

  // keep state and ref in-sync to avoid edge cases where a gesture updating the scroll detail is handled
  // inbetween a render cycle that changes the scroll detail and the useEffect that applies the detail
  const updateDeferredScrollDetail = useCallback((scrollDetail: ScrollDetail<K>) => {
    setDeferredScrollDetail(scrollDetail);
    deferredScrollDetailRef.current = scrollDetail;
  }, []);

  const {
    reorderProps,
    reorderKeyboardProps,
    reorderItemProps,
    reorderContext,
    reorderInstructions,
    reorderInstructionsId
  } = useItemReorder({
    rootRef,
    scrollerRef: viewportInfo.viewportConfig.scrollerRef,
    itemSelector: `[${LIST_ROW_SELECTOR}='${listId}']`,
    onReorder
  });

  // setup focus tracking using the useFocusHandling hook
  const {
    focusHandlingProps,
    activeCell,
    onActiveCellChanged,
    currentActiveCell,
    currentCell,
    isShowFocusRing,
    isTabbableMode,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge
  } = useFocusHandling({
    rootRef,
    viewportInfo,
    listId,
    listData,
    hasMore,
    selectionExtensionElementRef,
    currentItemOverride,
    onPersistCurrentItem,
    isEmbedded,
    itemEnterKeyFocusBehavior,
    isDataLoaded: !hasMore || listData.length > 0,
    reorderContext,
    deferredScrollDetail,
    deferredScrollDetailRef,
    updateDeferredScrollDetail
  });

  const keyboardHandlingProps = useKeyboardHandling({
    rootRef,
    viewportInfo,
    listId,
    listData,
    isTabbableMode,
    selectionExtensionElementRef,
    activeCell,
    onActiveCellChanged,
    isDropDownSelection: isEmbedded,
    selectionMode,
    selectedKeys,
    onListSelectionChange,
    onItemAction,
    updateDeferredScrollDetail,
    reorderKeyboardProps,
    isReorderEnabled: onReorder != null
  });

  const { pointerHandlingProps } = usePointerHandling({
    rootRef,
    listId,
    listData,
    selectionExtensionElementRef,
    activeCell,
    onActiveCellChanged,
    isDropDownSelection: isEmbedded,
    selectionMode,
    selectedKeys,
    onListSelectionChange,
    onItemAction
  });

  const scrollHandlingProps = useScrollHandling({
    rootRef,
    viewportInfo,
    listId,
    isLayoutRequired,
    scrollPositionOverride
  });

  const { contextMenuTriggerProps, menuProps, contextMenuContext } = useContextMenu<K, D>(
    rootRef,
    listId,
    listData,
    contextMenuConfig
  );

  const interactionManagerProps = mergeProps(
    focusHandlingProps,
    keyboardHandlingProps,
    pointerHandlingProps,
    reorderProps,
    contextMenuTriggerProps
  );

  // setup imperative handle implementation
  useImperativeHandle(
    ref!,
    () => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      return {
        focus: () => {
          if (rootElement != null) {
            if (currentActiveCell != null) {
              const cellElement = getElementFromCell(rootElement, listId, currentActiveCell);
              if (cellElement != null) {
                cellElement.focus();
                return;
              }
            }
            // if no current cell element was found, just focus the root element
            rootElement.focus();
          }
        },
        getScrollPosition: () => {
          if (rootElement == null || scrollerElement == null || isLayoutRequired) {
            return {};
          }
          return getScrollPosition<K>(rootElement, scrollerElement, viewportInfo, listId);
        }
      };
    },
    [rootRef, viewportInfo, listId, currentActiveCell, isLayoutRequired]
  );

  return {
    interactionManagerProps,
    scrollHandlingProps,
    onListSelectionChange,
    isShowFocusRing,
    isTabbableMode,
    currentCell,
    currentActiveCell,
    contextMenuContext,
    menuProps,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    disabledRowInteractionKeys,
    reorderItemProps,
    reorderInstructions,
    reorderInstructionsId
  };
}
